import { HttpErrorResponse } from "@angular/common/http";

export interface IUserAddSearchMessagesHelper {
  title: string;
  description: string;
}

export class UserAddSearchMessagesHelper {
  static readonly TOO_MANY_RESULTS_INLINE_MESSAGE =
    'Foram encontrados muitos resultados. Informe o nome, e-mail ou logon completo.';

  private readonly DEFAULT_ERROR: IUserAddSearchMessagesHelper = {
    title: "Não foi possível buscar o usuário.",
    description:
      "Estamos trabalhando para resolver esse problema. Por favor, tente novamente mais tarde e, se o erro persistir, entre em contato com a nossa equipe de atendimento.",
  };

  public status = 0;
  public errorObj: { code: string; description: string } | undefined;

  public descriptions: Record<number, Record<string, IUserAddSearchMessagesHelper>> = {
    400: {
      default: {
        ...this.DEFAULT_ERROR,
      },
      "61": {
        title: "Muitos resultados encontrados.",
        description: UserAddSearchMessagesHelper.TOO_MANY_RESULTS_INLINE_MESSAGE,
      },
      validation: {
        title: "Não foi possível realizar a busca.",
        description:
          "Os parâmetros da pesquisa são inválidos. Por favor, tente novamente.",
      },
    },
    401: {
      default: {
        title: "Sessão expirada.",
        description:
          "Sua sessão foi encerrada. Por favor, faça login novamente.",
      },
      "401": {
        title: "Sessão expirada.",
        description:
          "Sua sessão foi encerrada. Por favor, faça login novamente.",
      },
    },
    403: {
      default: {
        title: "Acesso negado.",
        description:
          "Você não tem permissão para acessar este recurso. Entre em contato com a nossa equipe de atendimento.",
      },
    },
    404: {
      default: {
        ...this.DEFAULT_ERROR,
      },
      "404": {
        title: "Usuário não encontrado.",
        description:
          "Por favor, pesquise um novo usuário para espelhar os acessos.",
      },
    },
    422: {
      default: {
        ...this.DEFAULT_ERROR,
      },
      "13": {
        title: "Esse usuário não pode ser espelhado.",
        description:
          "As informações de empresa do responsável estão incompletas. Entre em contato com a nossa equipe de atendimento.",
      },
      "28": {
        title: "Esse usuário não pode ser espelhado.",
        description:
          "É necessário esperar 24 horas para espelhar um usuário recém-criado.",
      },
      "29": {
        title: "Esse usuário não pode ser espelhado.",
        description:
          "Por favor, pesquise um novo usuário para espelhar os acessos.",
      },
      "30": {
        title: "Não é possível espelhar os acessos de um usuário bloqueado.",
        description:
          "Por favor, pesquise um novo usuário para espelhar os acessos.",
      },
      "37": {
        title: "Não foi possível criar o usuário.",
        description:
          "Você atingiu a quantidade máxima de usuários desse contrato, entre em contato com a nossa equipe de atendimento.",
      },
      "38": {
        title: "Não foi possível espelhar os acessos de um usuário pendente.",
        description:
          "Usuários com pendência de cadastro ou que nunca acessaram o Menu de Produtos não podem ser usados como espelho. Por favor, pesquise um novo usuário para espelhar os acessos.", // NOSONAR
      },
      "61": {
        title: "Muitos resultados encontrados.",
        description: UserAddSearchMessagesHelper.TOO_MANY_RESULTS_INLINE_MESSAGE,
      },
    },
    429: {
      default: {
        title: "Muitas tentativas.",
        description:
          "Você excedeu o limite de requisições. Por favor, aguarde alguns instantes e tente novamente.",
      },
    },
    500: {
      default: {
        ...this.DEFAULT_ERROR,
      },
    },
    502: {
      default: {
        ...this.DEFAULT_ERROR,
      },
    },
    503: {
      default: {
        title: "Serviço temporariamente indisponível.",
        description:
          "Estamos com instabilidade em um dos nossos sistemas. Por favor, tente novamente mais tarde e, se o erro persistir, entre em contato com a nossa equipe de atendimento.",
      },
      "503": {
        title: "Serviço temporariamente indisponível.",
        description:
          "Estamos com instabilidade em um dos nossos sistemas. Por favor, tente novamente mais tarde e, se o erro persistir, entre em contato com a nossa equipe de atendimento.",
      },
    },
    504: {
      default: {
        title: "Tempo de resposta excedido.",
        description:
          "O servidor não respondeu a tempo. Por favor, tente novamente mais tarde.",
      },
    },
  };

  constructor(error: HttpErrorResponse) {
    this.status = error.status;
    this.errorObj = UserAddSearchMessagesHelper.extractErrorObject(error);
  }

  static extractErrorObject(
    error: HttpErrorResponse,
  ): { code: string; description: string } | undefined {
    const body = error.error;

    if (Array.isArray(body) && body[0]) {
      return {
        code: body[0].code,
        description: body[0].description ?? body[0].message ?? '',
      };
    }

    if (body && typeof body === 'object' && body.code) {
      return {
        code: body.code,
        description: body.description ?? body.message ?? '',
      };
    }

    return undefined;
  }

  static isTooManyResultsError(error: HttpErrorResponse): boolean {
    return (
      (error.status === 400 || error.status === 422) &&
      UserAddSearchMessagesHelper.extractErrorObject(error)?.code === '61'
    );
  }

  get error(): IUserAddSearchMessagesHelper {
    const descriptionObj = this.descriptions[this.status];
    if (!descriptionObj) return this.DEFAULT_ERROR;

    const code = this.errorObj?.code;
    const apiDescription = this.errorObj?.description ?? '';

    if (this.status === 400) {
      if (code === '61') {
        return descriptionObj['61'];
      }
      if (
        apiDescription.includes('getManageableLogons.limit') ||
        apiDescription.includes('getManageableLogons.offset')
      ) {
        return descriptionObj['validation'] ?? descriptionObj['default'];
      }
    }

    if (this.status === 422 && code === '61') {
      return this.descriptions[422]['61'] ?? this.descriptions[400]['61'];
    }

    return (code && descriptionObj[code])
      ? descriptionObj[code]
      : descriptionObj['default'] ?? this.DEFAULT_ERROR;
  }
}
